/**
 * Coloanele grilei zilei, ca DATE.
 *
 * Erau șapte `<span>`-uri scrise de mână în antet și alte șapte în rând, plus un șablon CSS și un
 * `NUMAR_COLOANE` scrise separat. Patru locuri care trebuiau să rămână sincronizate manual — și
 * chiar au divergut: antetul aluneca o coloană la stânga, fiindcă o celulă ieșea din grid.
 *
 * Feedbackul colegelor cere încă trei coloane (Sold, Stoc, Valută), deci zece cu totul. Pe un
 * laptop de 1366px zece coloane fixe fac grila ilizibilă, iar coloana Furnizor — cea mai
 * importantă — se strânge prima. De aceea coloanele se pot porni și opri, iar alegerea intră în URL.
 *
 * O singură listă generează: șablonul CSS, antetul, celulele rândului, numărul de coloane pentru
 * cursor și indexul coloanei editabile. Nimic nu se mai poate desincroniza, pentru că nu mai există
 * două surse.
 */

export type CheieColoana =
  | 'selectie'
  | 'stare'
  | 'furnizor'
  | 'suma'
  | 'valuta'
  | 'sold'
  | 'stoc'
  | 'facturi'
  | 'banca'
  | 'note';

export type CheieSortare = 'stare' | 'furnizor' | 'suma' | 'facturi' | 'banca' | 'sold' | 'stoc';

export interface DefinitieColoana {
  cheie: CheieColoana;
  eticheta: string;
  /** Pista din `grid-template-columns`. */
  pista: string;
  /** Clasa de aliniere. Trebuie să fie IDENTICĂ în antet și în celulă. */
  aliniere?: string;
  /** Eticheta se citește doar de cititorul de ecran (coloana de selecție). */
  ascunsa?: boolean;
  /** Cheia de sortare; lipsă = coloana nu se sortează. */
  sortare?: CheieSortare;
  /** Coloanele care nu se pot ascunde: fără ele ecranul nu mai răspunde la întrebarea lui. */
  fixa?: boolean;
  /** Explicație pentru comutator. */
  descriere?: string;
}

export const COLOANE_ZI: readonly DefinitieColoana[] = [
  { cheie: 'selectie', eticheta: 'Selectează', pista: '2rem', ascunsa: true, fixa: true },
  { cheie: 'stare', eticheta: 'Stare', pista: '9rem', sortare: 'stare', fixa: true },
  {
    cheie: 'furnizor',
    eticheta: 'Furnizor',
    pista: 'minmax(12rem,1fr)',
    sortare: 'furnizor',
    fixa: true,
  },
  {
    cheie: 'suma',
    eticheta: 'Sumă',
    pista: '9rem',
    aliniere: 'justify-self-end',
    sortare: 'suma',
    fixa: true,
  },
  {
    cheie: 'valuta',
    eticheta: 'Valută',
    // Trebuie sa incapa cursul (6 zecimale), butonul 'Preia BNR' si suma in lei, una sub alta.
    pista: '13rem',
    descriere: 'Cursul și echivalentul din cealaltă monedă. Se completează în ambele sensuri.',
  },
  {
    cheie: 'sold',
    eticheta: 'Sold',
    pista: '7rem',
    aliniere: 'justify-self-end',
    sortare: 'sold',
    descriere: 'Soldul WME din ultimul snapshot săptămânal.',
  },
  {
    cheie: 'stoc',
    eticheta: 'Stoc',
    pista: '7rem',
    aliniere: 'justify-self-end',
    sortare: 'stoc',
    descriere: 'Stocul EVO. Roșu când depășește soldul — nu se plătește marfă nevândută.',
  },
  {
    cheie: 'facturi',
    eticheta: 'Facturi',
    pista: 'minmax(8rem,14rem)',
    sortare: 'facturi',
    descriere: 'Numerele de factură extrase din comentarii.',
  },
  {
    cheie: 'banca',
    eticheta: 'Bancă',
    pista: '7rem',
    sortare: 'banca',
    descriere: 'Codul băncii furnizorului. Editabil.',
  },
  { cheie: 'note', eticheta: 'Note', pista: '6rem', aliniere: 'justify-self-end', fixa: true },
];

const DUPA_CHEIE = new Map(COLOANE_ZI.map((c) => [c.cheie, c]));

/** Ce e pornit când nimeni n-a ales nimic. */
export const IMPLICITE: readonly CheieColoana[] = [
  'selectie',
  'stare',
  'furnizor',
  'suma',
  'sold',
  'stoc',
  'facturi',
  'banca',
  'note',
];

/** Coloanele care apar în comutator, în ordinea din grilă. */
export const COMUTABILE: readonly DefinitieColoana[] = COLOANE_ZI.filter((c) => c.fixa !== true);

/**
 * Coloanele active, în ordinea din grilă.
 *
 * `alese` vine din URL și poate conține orice — se filtrează la chei cunoscute, iar ordinea e mereu
 * cea a grilei, nu cea în care au fost bifate. Coloanele fixe intră întotdeauna: fără Furnizor și
 * Sumă ecranul nu mai răspunde la „cui plătesc și cât".
 *
 * `areValuta` pornește coloana `Valută` automat când ziua chiar are o plată în altă monedă. E
 * singura coloană cu comportament implicit contextual, și e deliberat: pe zilele numai în lei ar fi
 * o coloană de liniuțe, iar pe zilele cu EUR e exact ce trebuie completat.
 */
export function coloaneActive(
  alese: readonly string[] | undefined,
  areValuta: boolean,
): readonly DefinitieColoana[] {
  const set =
    alese === undefined
      ? new Set<string>([...IMPLICITE, ...(areValuta ? ['valuta'] : [])])
      : new Set<string>(alese);

  return COLOANE_ZI.filter((c) => c.fixa === true || set.has(c.cheie));
}

/**
 * Valoarea pentru `grid-template-columns`, generata din coloanele active.
 *
 * Se aplica prin `style`, NU printr-o clasa `grid-cols-[…]` — si asta nu e o preferinta.
 * Tailwind genereaza clasele scanand codul SURSA; o clasa construita la executie nu exista in CSS,
 * iar grila se prabuseste tacit: toate celulele ajung in prima coloana, una peste alta. Cat timp
 * coloanele erau fixe, sablonul era un literal si mergea; din clipa in care devin dinamice, nu mai
 * are cum.
 */
export function sablonColoane(active: readonly DefinitieColoana[]): string {
  return active.map((c) => c.pista).join(' ');
}

/** Indexul unei coloane în setul activ; `-1` când e oprită. */
export function indexColoana(
  active: readonly DefinitieColoana[],
  cheie: CheieColoana,
): number {
  return active.findIndex((c) => c.cheie === cheie);
}

export function definitieColoana(cheie: CheieColoana): DefinitieColoana {
  const gasita = DUPA_CHEIE.get(cheie);
  if (!gasita) throw new Error(`coloană necunoscută: ${cheie}`);
  return gasita;
}
